<template>
    <docu-layout title="Privacy Policy">
        <template #default>
            <div class="privacy-container">
                <h1 class="privacy-title">Privacy Policy</h1>
                <p class="intro-text">
                    At {{ Ajility.DBAName }}, a service of {{ Ajility.ParentCompany }}, your privacy is very important
                    to us. Our goal is to provide a secure and seamless experience for managing your documents, while
                    ensuring your personal information is handled with care and respect. This Privacy Policy explains
                    how we collect, use, disclose, and safeguard your information when you visit our website,
                    <a :href="`${Ajility.BaseUrl}`" class="link">{{ Ajility.BaseUrl }}</a> (the “Site”), or use our
                    services.
                </p>
                <p class="intro-text">
                    By accessing the Site or using our services, you consent to the terms of this Privacy Policy. If you
                    do not agree, please do not use the Site or services.
                </p>

                <section>
                    <h2>Our Commitment to Your Privacy</h2>
                    <p>
                        {{ Ajility.DBAName }} is committed to ensuring the accuracy, confidentiality, and integrity of
                        your personal information. We carefully control the collection, use, and disclosure of personal
                        information provided by our users.
                    </p>
                    <p>
                        We do not sell, rent, or exchange your personal information for commercial purposes. Information
                        collected from you helps us improve our services and provide you with the best possible
                        experience.
                    </p>
                </section>

                <section>
                    <h2>SMS Verification</h2>
                    <p>
                        When you create an account with us, we may collect your phone number to send a one-time
                        verification code via SMS to confirm your identity. This verification message will only be sent
                        during the account registration or login process.
                    </p>
                    <p class="mt-2">
                        We do not share your phone number with third parties, and we do not use it for marketing
                        purposes. Your number is used exclusively for account security and access.
                    </p>
                    <p class="mt-2">
                        Standard message and data rates may apply depending on your mobile carrier. If you have
                        questions about your data, please refer to our full Privacy Policy.
                    </p>
                </section>

                <section>
                    <h2>What is Personal Information?</h2>
                    <p>
                        Personal information is any information that directly identifies you, such as your name, email
                        address, billing information, or documents you upload. It may also include information about
                        your account usage, subscriptions, and interactions with the Site.
                    </p>
                    <p>
                        Publicly available information, such as directory listings, is not considered personal
                        information under this policy.
                    </p>
                </section>

                <section>
                    <h2>Information We Collect</h2>
                    <h3>Information You Provide</h3>
                    <p>We collect personal information when you:</p>
                    <ul>
                        <li>Create an account on {{ Ajility.DBAName }}.</li>
                        <li>Upload, share, or manage documents on the platform.</li>
                        <li>Subscribe to newsletters or updates.</li>
                        <li>Communicate with us via email, forms, or support requests.</li>
                        <li>Use our services, such as making payments via integrated third-party services.</li>
                    </ul>

                    <h3>Automatic Information Collection</h3>
                    <p>We collect certain information automatically when you visit the Site:</p>
                    <ul>
                        <li>
                            <strong>Cookies and Tracking Technologies:</strong> We use cookies to enhance your
                            experience and track usage patterns. Cookies allow us to recognize you when you sign in and
                            personalize your interactions.
                        </li>
                        <li>
                            <strong>Usage Data:</strong> This includes IP addresses, browser types, access times, and
                            pages visited.
                        </li>
                        <li>
                            <strong>Third-Party Analytics:</strong> We use third-party tools, such as Google Analytics,
                            to understand usage trends and improve our services. These tools may collect anonymized
                            data.
                        </li>
                    </ul>

                    <h3>Payment Information</h3>
                    <p>
                        Payments for {{ Ajility.DBAName }} services are processed through third-party providers such as
                        <strong>Stripe</strong>. We do not store credit card details on our servers. Any information you
                        provide to third-party payment processors is governed by their privacy policies.
                        {{ Ajility.DBAName }} is not responsible for the data collected or used by these third-party
                        providers.
                    </p>
                </section>

                <section>
                    <h2>How We Use Your Information</h2>
                    <p>We collect and use your personal information primarily to:</p>
                    <ul>
                        <li>Provide and improve our services.</li>
                        <li>Manage your account and authenticate access.</li>
                        <li>Communicate with you about updates, promotions, or technical issues.</li>
                        <li>Facilitate payments and billing.</li>
                        <li>Ensure compliance with our Terms of Service and applicable laws.</li>
                    </ul>
                </section>

                <section>
                    <h2>Disclosure of Personal Information</h2>
                    <p>We may share your personal information under the following circumstances:</p>
                    <ul>
                        <li>
                            <strong>Third-Party Service Providers:</strong> We share necessary information with trusted
                            partners who perform services on our behalf, such as payment processing (e.g., Stripe),
                            email delivery, and cloud storage. These providers are bound by confidentiality agreements
                            and may only use your data for specified purposes.
                        </li>
                        <li>
                            <strong>Legal Obligations:</strong> We may disclose your information when required to comply
                            with legal obligations, enforce our policies, or protect the rights and safety of our users.
                        </li>
                        <li>
                            <strong>Business Transfers:</strong> In the event of a merger, acquisition, or sale of
                            assets, your personal information may be transferred to the new entity.
                        </li>
                    </ul>
                </section>

                <section>
                    <h2>How We Protect Your Information</h2>
                    <p>{{ Ajility.DBAName }} uses industry-standard measures to secure your information, including:</p>
                    <ul>
                        <li>Encrypted communication channels (e.g., HTTPS).</li>
                        <li>Password-protected accounts.</li>
                        <li>Limited access to personal information on a need-to-know basis.</li>
                        <li>Regular security audits and updates.</li>
                    </ul>
                    <p>
                        Despite our efforts, no security measure is 100% effective. By using {{ Ajility.DBAName }}, you
                        acknowledge and accept the risks of transmitting personal information over the internet.
                    </p>
                </section>

                <section>
                    <h2>Your Choices and Rights</h2>
                    <h3>Access and Updates</h3>
                    <p>You can view, update, or delete your personal information by logging into your account.</p>

                    <h3>Opt-Out of Communications</h3>
                    <p>
                        You may opt out of promotional emails by clicking the "unsubscribe" link in the email. Note that
                        you may still receive essential account-related communications.
                    </p>

                    <h3>Cookies</h3>
                    <p>
                        You can manage or disable cookies in your browser settings. Disabling cookies may affect the
                        functionality of the Site.
                    </p>
                </section>

                <section>
                    <h2>Children's Privacy</h2>
                    <p>
                        {{ Ajility.DBAName }} does not knowingly collect personal information from children under 13
                        years of age. If we learn that we have inadvertently collected information from a child under
                        13, we will delete it promptly. If you are a parent or guardian and believe your child has
                        provided us with personal information, please contact us.
                    </p>
                </section>

                <section>
                    <h2>Third-Party Services</h2>
                    <p>
                        {{ Ajility.DBAName }} integrates with third-party services like Stripe for payment processing
                        and Google Analytics for usage tracking. These services operate independently and are governed
                        by their own privacy policies. We recommend reviewing their policies to understand how they
                        handle your data.
                    </p>
                    <p>
                        {{ Ajility.DBAName }} is not responsible for the privacy practices or content of these
                        third-party services.
                    </p>
                </section>

                <section>
                    <h2>Changes to This Privacy Policy</h2>
                    <p>
                        We may update this Privacy Policy from time to time to reflect changes in our practices or for
                        legal reasons. Changes will be posted on this page with the "Last Updated" date. Continued use
                        of the Site or services after updates constitutes acceptance of the revised policy.
                    </p>
                </section>

                <footer>
                    <h2>Contact Us</h2>
                    <address-block />
                    <p><strong>Last Updated:</strong> June 09, 2025</p>
                </footer>
            </div>
        </template>
    </docu-layout>
</template>

<script setup lang="ts">
import DocuLayout from '@/Layouts/DocuLayout.vue'
import { Ajility } from '@/Enums/Ajility'
import { useHead } from '@vueuse/head'
import { schemaTagOptions } from '@/Composables/SchemaTagOptions'
import AddressBlock from '@/Pages/AddressBlock.vue'

useHead(
    schemaTagOptions({
        title: 'Privacy Policy | Docu-Vault',
        description:
            "Review Docu-Vault's Privacy Policy to learn how we collect, use, and safeguard your personal information. Understand your privacy rights and our data protection practices.",
        schemaType: 'WebPage',
        robots: 'index, follow',
    }),
)
</script>

<style scoped>
.privacy-container {
    max-width: 75%;
    margin: 0 auto;
    padding: 2rem;
    font-family: Arial, sans-serif;
    line-height: 1.6;
    background-color: #f9f9f9;
    border-radius: 8px;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
}

.privacy-title {
    font-size: 2rem;
    font-weight: bold;
    text-align: center;
    margin-bottom: 1.5rem;
    color: #333;
}

.intro-text {
    font-size: 1.2rem;
    color: #555;
    margin-bottom: 1.5rem;
}

h2 {
    font-size: 1.5rem;
    color: #444;
    margin-top: 2rem;
}

h3 {
    font-size: 1.3rem;
    color: #555;
    margin-top: 1.5rem;
}

ul {
    margin-left: 1.5rem;
    margin-bottom: 1.5rem;
}

ul li {
    margin-bottom: 0.5rem;
}

.link {
    color: #007bff;
    text-decoration: none;
}

.link:hover {
    text-decoration: underline;
}

footer {
    margin-top: 2rem;
    text-align: center;
    font-size: 0.9rem;
    color: #666;
}

address {
    font-style: normal;
    line-height: 1.5;
    margin-bottom: 1rem;
}
</style>
